import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosConfig";

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("access_token");
  const [userData, setUserData] = useState(null);
  const [profileComplete, setProfileComplete] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  const publicRoutes = [
    '/register',
    '/login',
    '/profile-setup',
    '/checkout',
    '/privacy-policy',
    '/terms-of-service'
  ];

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!token) {
        setLoading(false);
        setProfileComplete(false);
        setIsSubscribed(false);
        return;
      }

      try {
        // First get current user data
        const userResponse = await axiosInstance.get('/auth/current-user/');
        setUserData(userResponse.data);
        console.log('User data:', userResponse.data);

        // Check profile completion
        const profileResponse = await axiosInstance.get("/check-profile-complete/");
        setProfileComplete(profileResponse.data.profile_complete);
        console.log('Profile status:', profileResponse.data);

        // Check subscription only if profile is complete
        if (profileResponse.data.profile_complete) {
          try {
            // Make sure this matches your Django URL pattern exactly
            const subscriptionResponse = await axiosInstance.get("/check-subscription-status/", {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            console.log('Subscription response:', subscriptionResponse.data);
            setIsSubscribed(subscriptionResponse.data.is_subscribed);
          } catch (subError) {
            console.error('Subscription check error:', subError.response || subError);
            setIsSubscribed(false);
            // Don't set error here to avoid login redirect
          }
        }
      } catch (error) {
        console.error("Error in checkUserStatus:", error.response || error);
        if (error.response?.status === 401) {
          localStorage.removeItem("access_token");
        }
        setProfileComplete(false);
        setIsSubscribed(false);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle public routes first
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  // Handle authentication
  if (!token || !userData) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Handle profile completion
  if (!profileComplete && location.pathname !== "/profile-setup") {
    return <Navigate to="/profile-setup" />;
  }

  // Handle subscription check
  if (profileComplete && !isSubscribed && location.pathname !== "/checkout") {
    return <Navigate to="/checkout" />;
  }

  // Special case: After completing profile
  if (profileComplete && location.pathname === "/profile-setup") {
    return <Navigate to="/checkout" />;
  }

  // Allow access if all conditions are met
  return children;
};

export default ProtectedRoute;